import React, { createContext, useState, useReducer } from 'react';

// État initial du contexte de réservation
const initialState = {
  currentStep: 1,
  practitioner: null,
  service: null,
  serviceTitle: null,
  duration: null,
  price: null,
  remotePrice: null,
  homePrice: null,
  homeTransportFee: 0,
  homeRadius: 0,
  restTime: 0,
  date: null,
  time: null,
  formattedDate: null,
  name: null,
  email: null,
  phone: null,
  notes: null,
  
  // Nouveaux champs pour les modes de consultation
  consultationMode: null, // 'cabinet', 'remote', 'home'
  isRemote: false, // Pour la compatibilité avec l'ancien système
  isHomeVisit: false, // Pour les visites à domicile
  clientAddress: null, // Adresse du client pour les visites à domicile
  
  // Autres champs
  serviceNotes: null,
  reminderSent: false
};

// Actions pour le reducer
const BOOKING_ACTIONS = {
  SET_STEP: 'SET_STEP',
  UPDATE_DATA: 'UPDATE_DATA',
  SET_PRACTITIONER: 'SET_PRACTITIONER',
  SET_SERVICE: 'SET_SERVICE',
  RESET_BOOKING: 'RESET_BOOKING'
};

// Reducer pour gérer les mises à jour d'état
const bookingReducer = (state, action) => {
  switch (action.type) {
    case BOOKING_ACTIONS.SET_STEP:
      return {
        ...state,
        currentStep: action.payload
      };
      
    case BOOKING_ACTIONS.UPDATE_DATA:
      console.log('BookingContext: Mise à jour des données:', action.payload);
      return {
        ...state,
        ...action.payload
      };
      
    case BOOKING_ACTIONS.SET_PRACTITIONER:
      console.log('BookingContext: Définition du praticien:', action.payload);
      return {
        ...state,
        practitioner: action.payload,
        // Réinitialiser les données dépendantes
        service: null,
        serviceTitle: null,
        duration: null,
        price: null,
        remotePrice: null,
        homePrice: null,
        homeTransportFee: 0,
        date: null,
        time: null,
        formattedDate: null,
        consultationMode: null,
        isRemote: false,
        isHomeVisit: false
      };
      
    case BOOKING_ACTIONS.SET_SERVICE:
      console.log('BookingContext: Définition du service:', action.payload);
      const serviceData = action.payload;
      
      // Déterminer automatiquement isRemote et isHomeVisit selon consultationMode
      const isRemote = serviceData.consultationMode === 'remote';
      const isHomeVisit = serviceData.consultationMode === 'home';
      
      return {
        ...state,
        service: serviceData.id,
        serviceTitle: serviceData.title,
        duration: serviceData.duration,
        price: serviceData.price,
        remotePrice: serviceData.remotePrice || serviceData.price,
        homePrice: serviceData.homePrice || serviceData.price,
        homeTransportFee: serviceData.homeTransportFee || 0,
        homeRadius: serviceData.homeRadius || 0,
        restTime: serviceData.restTime || 0,
        notes: serviceData.notes || null,
        consultationMode: serviceData.consultationMode || 'cabinet',
        isRemote: isRemote,
        isHomeVisit: isHomeVisit,
        // Réinitialiser les données dépendantes
        date: null,
        time: null,
        formattedDate: null
      };
      
    case BOOKING_ACTIONS.RESET_BOOKING:
      console.log('BookingContext: Réinitialisation de la réservation');
      return initialState;
      
    default:
      return state;
  }
};

// Création du contexte
export const BookingContext = createContext();

// Provider du contexte
export const BookingProvider = ({ children }) => {
  const [bookingData, dispatch] = useReducer(bookingReducer, initialState);

  // Fonction pour définir l'étape actuelle
  const setCurrentStep = (step) => {
    console.log('BookingContext: Changement d\'étape:', step);
    dispatch({
      type: BOOKING_ACTIONS.SET_STEP,
      payload: step
    });
  };

  // Fonction pour mettre à jour les données de réservation
  const updateBookingData = (data) => {
    console.log('BookingContext: Mise à jour des données de réservation:', data);
    
    // Validation et normalisation des données
    const normalizedData = { ...data };
    
    // Gérer la cohérence entre consultationMode et les flags isRemote/isHomeVisit
    if (normalizedData.consultationMode) {
      normalizedData.isRemote = normalizedData.consultationMode === 'remote';
      normalizedData.isHomeVisit = normalizedData.consultationMode === 'home';
    }
    
    // Si isRemote est défini, mettre à jour consultationMode
    if (normalizedData.isRemote !== undefined && normalizedData.consultationMode === undefined) {
      normalizedData.consultationMode = normalizedData.isRemote ? 'remote' : 'cabinet';
    }
    
    dispatch({
      type: BOOKING_ACTIONS.UPDATE_DATA,
      payload: normalizedData
    });
  };

  // Fonction pour définir le praticien sélectionné
  const setSelectedPractitioner = (practitioner) => {
    console.log('BookingContext: Sélection du praticien:', practitioner);
    dispatch({
      type: BOOKING_ACTIONS.SET_PRACTITIONER,
      payload: practitioner
    });
  };

  // Fonction pour définir le service sélectionné
  const setSelectedService = (service) => {
    console.log('BookingContext: Sélection du service:', service);
    dispatch({
      type: BOOKING_ACTIONS.SET_SERVICE,
      payload: service
    });
  };

  // Fonction pour réinitialiser toutes les données de réservation
  const resetBookingData = () => {
    console.log('BookingContext: Réinitialisation complète');
    dispatch({
      type: BOOKING_ACTIONS.RESET_BOOKING
    });
  };

  // Fonctions utilitaires pour vérifier le mode de consultation
  const isRemoteConsultation = () => {
    return bookingData.consultationMode === 'remote' || bookingData.isRemote;
  };

  const isHomeVisitConsultation = () => {
    return bookingData.consultationMode === 'home' || bookingData.isHomeVisit;
  };

  const isCabinetConsultation = () => {
    return bookingData.consultationMode === 'cabinet' || 
           (!bookingData.isRemote && !bookingData.isHomeVisit);
  };

  // Fonction pour obtenir le libellé du mode de consultation
// Fonction pour obtenir le libellé du mode de consultation
  const getConsultationModeLabel = (mode) => {
    switch (mode) {
      case 'cabinet':
        return '🏢 En cabinet';
      case 'remote':
        return '💻 À distance';
      case 'home':
        return '🏠 À domicile';
      default:
        return '🏢 En cabinet';
    }
  };

  // Fonction pour calculer le prix total selon le mode
  const getTotalPrice = () => {
    let totalPrice = bookingData.price || 0;
    
    // Ajouter les frais de transport pour les visites à domicile
    if (isHomeVisitConsultation() && bookingData.homeTransportFee) {
      totalPrice += bookingData.homeTransportFee;
    }
    
    return totalPrice;
  };

  // Valeur du contexte
  const contextValue = {
    // État
    bookingData,
    currentStep: bookingData.currentStep,
    
    // Actions principales
    setCurrentStep,
    updateBookingData,
    setSelectedPractitioner,
    setSelectedService,
    resetBookingData,
    
    // Fonctions utilitaires
    isRemoteConsultation,
    isHomeVisitConsultation,
    isCabinetConsultation,
    getConsultationModeLabel,
    getTotalPrice,
    
    // Pour la compatibilité avec l'ancien code
    practitioner: bookingData.practitioner,
    service: bookingData.service,
    date: bookingData.date,
    time: bookingData.time
  };

  return (
    <BookingContext.Provider value={contextValue}>
      {children}
    </BookingContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useBooking = () => {
  const context = React.useContext(BookingContext);
  
  if (!context) {
    throw new Error('useBooking doit être utilisé dans un BookingProvider');
  }
  
  return context;
};